import { apiClient } from './api'
import { formatMoney } from '@/utils/formatMoney'
import type {
  Shop,
  ShopListItem,
  ShopStatistics,
  ShopReadiness,
  ShopStatus,
  CreateShopRequest,
  UpdateShopRequest,
  PaginatedResult,
  StripeAccountStatus,
  ShopImage,
  ShopImageType,
  ShopPaymentSettings,
  UpdatePaymentSettingsRequest,
  ValidateStripeKeysRequest,
  ValidateStripeKeysResult,
  ShopTermsAndConditions,
  UpdateShopTermsRequest,
  ShopDocument
} from '@/types/shop'

export const shopService = {
  // ============================================================================
  // Provider Shop Management
  // ============================================================================

  /**
   * Get current provider's shop (LEGACY — a provider may own up to 2 shops).
   * Prefer getMyShops(). Kept for older callers that haven't migrated yet.
   */
  async getMyShop(): Promise<Shop | null> {
    try {
      const response = await apiClient.get<Shop>('/shop/shops/my-shop')
      return this._transformShopUrls(response)
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Get all shops (0, 1, or 2) owned by the current provider.
   * Retail shop comes first, wholesale second when both exist.
   */
  async getMyShops(): Promise<Shop[]> {
    try {
      const shops = await apiClient.get<Shop[]>('/shop/shops/my-shops')
      return (shops || []).map(s => this._transformShopUrls(s))
    } catch (error: any) {
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  },

  /**
   * Get shop by ID
   */
  async getShop(id: string): Promise<Shop> {
    const shop = await apiClient.get<Shop>(`/shop/shops/${id}`)
    return this._transformShopUrls(shop)
  },

  /**
   * Get a single provider's shop by provider id (LEGACY — admin impersonation).
   * Prefer getShopsByProviderId().
   */
  async getShopByProviderId(providerId: string): Promise<Shop | null> {
    try {
      const shop = await apiClient.get<Shop>(`/shop/shops/provider/${providerId}/shop`)
      return this._transformShopUrls(shop)
    } catch {
      return null
    }
  },

  /**
   * Get all shops (0, 1, or 2) owned by a given provider (Admin only).
   * Used by admin impersonation to hydrate the shop switcher for the target provider.
   */
  async getShopsByProviderId(providerId: string): Promise<Shop[]> {
    try {
      const shops = await apiClient.get<Shop[]>(`/shop/shops/provider/${providerId}/shops`)
      return (shops || []).map(s => this._transformShopUrls(s))
    } catch {
      return []
    }
  },

  /**
   * Create a new shop
   */
  async createShop(data: CreateShopRequest): Promise<Shop> {
    const shop = await apiClient.post<Shop>('/shop/shops', data)
    return this._transformShopUrls(shop)
  },

  /**
   * Clone the caller's existing shop into a new sibling shop
   * (retail ↔ wholesale). Deep-copies settings, categories, products (base +
   * variants + images), and delivery tiers. Prices reset to £99.00, stock to 0.
   * Custom domain / subdomain / order + invoice counters / orders / staff /
   * invitations are NOT cloned.
   */
  async cloneShop(data: {
    sourceShopId: string
    name: string
    shopCode?: string
    isWholesaler: boolean
  }): Promise<Shop> {
    const shop = await apiClient.post<Shop>('/shop/shops/clone', data)
    return this._transformShopUrls(shop)
  },

  /**
   * Update shop
   */
  async updateShop(id: string, data: UpdateShopRequest): Promise<Shop> {
    const shop = await apiClient.put<Shop>(`/shop/shops/${id}`, data)
    return this._transformShopUrls(shop)
  },

  /**
   * Provider-facing: migrate this shop's RegionCode + Currency snapshot
   * post-creation. Same DTO + hard `confirmedNoBackfill` gate as the
   * admin endpoint (`adminShopService.updateShopRegion`) — BE rejects with
   * 400 if the acknowledgement checkbox wasn't ticked. Historic orders /
   * invoices / credit notes / payments keep their existing currency
   * snapshot; only NEW documents raised after this call use the new code.
   * Consumed by `RegionSettingsPanel` (variant="provider") inside
   * ShopSettingsView's "Region & Locale (Advanced)" fieldset.
   */
  async updateShopRegion(
    shopId: string,
    request: {
      regionCode: string
      currency: string
      timezone: string
      confirmedNoBackfill: boolean
    }
  ): Promise<Shop> {
    const shop = await apiClient.put<Shop>(`/shop/shops/${shopId}/region`, request)
    return this._transformShopUrls(shop)
  },

  /**
   * Activate shop
   */
  async activateShop(id: string): Promise<Shop> {
    const shop = await apiClient.post<Shop>(`/shop/shops/${id}/activate`)
    return this._transformShopUrls(shop)
  },

  /**
   * Deactivate shop
   */
  async deactivateShop(id: string): Promise<Shop> {
    const shop = await apiClient.post<Shop>(`/shop/shops/${id}/deactivate`)
    return this._transformShopUrls(shop)
  },

  /**
   * Get shop statistics
   */
  async getShopStatistics(id: string, fromDate?: string, toDate?: string): Promise<ShopStatistics> {
    const params = new URLSearchParams()
    if (fromDate) params.append('fromDate', fromDate)
    if (toDate) params.append('toDate', toDate)
    const queryString = params.toString()
    const url = `/shop/shops/${id}/statistics${queryString ? `?${queryString}` : ''}`
    return await apiClient.get<ShopStatistics>(url)
  },

  /**
   * Get shop readiness status for activation
   */
  async getShopReadiness(id: string): Promise<ShopReadiness> {
    return await apiClient.get<ShopReadiness>(`/shop/shops/${id}/readiness`)
  },

  /**
   * Check if shop code is available
   */
  async checkShopCodeAvailability(shopCode: string): Promise<{ available: boolean; message: string }> {
    return await apiClient.get(`/shop/shops/check-code/${shopCode}`)
  },

  // ============================================================================
  // Admin Shop Management
  // ============================================================================

  /**
   * Get all shops (admin only)
   */
  async getAllShops(params: {
    search?: string
    status?: ShopStatus
    page?: number
    pageSize?: number
    sortBy?: string
    sortDirection?: 'asc' | 'desc'
  } = {}): Promise<PaginatedResult<ShopListItem>> {
    const result = await apiClient.get<PaginatedResult<ShopListItem>>('/shop/shops', { params })
    return {
      ...result,
      items: result.items.map(shop => this._transformShopUrls(shop))
    }
  },

  // ============================================================================
  // Public Shop API
  // ============================================================================

  /**
   * Get public shop by shop code
   */
  async getPublicShop(shopCode: string): Promise<Shop> {
    const shop = await apiClient.get<Shop>(`/shops/${shopCode}`)
    return this._transformShopUrls(shop)
  },

  // ============================================================================
  // Stripe Connect
  // ============================================================================

  /**
   * Create Stripe Connect account
   */
  async createStripeConnectAccount(): Promise<{ accountId: string; message: string }> {
    return await apiClient.post('/shop/stripe/connect')
  },

  /**
   * Get Stripe onboarding link
   */
  async getStripeOnboardingLink(returnUrl: string, refreshUrl: string): Promise<{ url: string }> {
    return await apiClient.get('/shop/stripe/onboarding-link', {
      params: { returnUrl, refreshUrl }
    })
  },

  /**
   * Get Stripe account status
   */
  async getStripeAccountStatus(): Promise<StripeAccountStatus> {
    return await apiClient.get<StripeAccountStatus>('/shop/stripe/account-status')
  },

  /**
   * Check if provider can accept orders
   */
  async canAcceptOrders(): Promise<{ canAcceptOrders: boolean; message: string }> {
    return await apiClient.get('/shop/stripe/can-accept-orders')
  },

  /**
   * Get provider's Stripe account status (admin only)
   */
  async getProviderStripeAccountStatus(providerId: string): Promise<StripeAccountStatus> {
    return await apiClient.get<StripeAccountStatus>(`/shop/stripe/provider/${providerId}/account-status`)
  },

  // ============================================================================
  // Payment Settings
  // ============================================================================

  /**
   * Get payment settings for a shop
   */
  async getPaymentSettings(shopId: string): Promise<ShopPaymentSettings> {
    return await apiClient.get<ShopPaymentSettings>(`/shop/shops/${shopId}/payment-settings`)
  },

  /**
   * Update payment settings for a shop
   */
  async updatePaymentSettings(shopId: string, request: UpdatePaymentSettingsRequest): Promise<ShopPaymentSettings> {
    return await apiClient.put<ShopPaymentSettings>(`/shop/shops/${shopId}/payment-settings`, request)
  },

  /**
   * Validate provider's own Stripe keys
   */
  async validateStripeKeys(request: ValidateStripeKeysRequest): Promise<ValidateStripeKeysResult> {
    return await apiClient.post<ValidateStripeKeysResult>('/shop/shops/validate-stripe-keys', request)
  },

  // ============================================================================
  // Terms & Conditions
  // ============================================================================

  /**
   * Get terms and conditions for a shop
   */
  async getTermsAndConditions(shopId: string): Promise<ShopTermsAndConditions> {
    return await apiClient.get<ShopTermsAndConditions>(`/shop/shops/${shopId}/terms`)
  },

  /**
   * Update terms and conditions for a shop
   */
  async updateTermsAndConditions(shopId: string, request: UpdateShopTermsRequest): Promise<ShopTermsAndConditions> {
    return await apiClient.put<ShopTermsAndConditions>(`/shop/shops/${shopId}/terms`, request)
  },

  // ============================================================================
  // Shop Images
  // ============================================================================

  /**
   * Get base URL for image transformation
   */
  _getBaseUrl(): string {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:50000/api'
    // Remove /api suffix to get the base server URL
    return apiUrl.replace(/\/api\/?$/, '')
  },

  /**
   * Transform a single URL from relative to absolute
   */
  _transformUrl(url: string | null | undefined): string | null {
    if (!url) return null
    if (url.startsWith('http')) return url
    return `${this._getBaseUrl()}${url}`
  },

  /**
   * Transform Shop's logoUrl and bannerUrl to absolute URLs
   */
  _transformShopUrls<T extends Shop | ShopListItem>(shop: T): T {
    const transformed = {
      ...shop,
      logoUrl: this._transformUrl(shop.logoUrl) ?? undefined
    } as T
    // bannerUrl only exists on Shop, not ShopListItem
    if ('bannerUrl' in shop) {
      (transformed as Shop).bannerUrl = this._transformUrl((shop as Shop).bannerUrl) ?? undefined
    }
    return transformed
  },

  /**
   * Transform relative image URL to absolute URL
   * The API returns relative paths like /images/... but we need absolute URLs
   */
  _transformImageUrl(image: ShopImage | null): ShopImage | null {
    if (!image) return null
    return {
      ...image,
      url: image.url.startsWith('http') ? image.url : `${this._getBaseUrl()}${image.url}`
    }
  },

  /**
   * Get all images for a shop
   */
  async getShopImages(shopId: string, type?: ShopImageType): Promise<ShopImage[]> {
    const params = type ? { type } : {}
    const images = await apiClient.get<ShopImage[]>(`/shop/shops/${shopId}/images`, { params })
    return images.map(img => this._transformImageUrl(img)!)
  },

  /**
   * Get shop logo
   */
  async getShopLogo(shopId: string): Promise<ShopImage | null> {
    try {
      const image = await apiClient.get<ShopImage>(`/shop/shops/${shopId}/images/logo`)
      return this._transformImageUrl(image)
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Get shop banner
   */
  async getShopBanner(shopId: string): Promise<ShopImage | null> {
    try {
      const image = await apiClient.get<ShopImage>(`/shop/shops/${shopId}/images/banner`)
      return this._transformImageUrl(image)
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Upload shop image (logo, banner, or gallery)
   */
  async uploadShopImage(
    shopId: string,
    file: File,
    type: ShopImageType,
    altText?: string,
    setAsPrimary?: boolean
  ): Promise<ShopImage> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    if (altText) formData.append('altText', altText)
    if (setAsPrimary) formData.append('setAsPrimary', 'true')

    const image = await apiClient.post<ShopImage>(`/shop/shops/${shopId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return this._transformImageUrl(image)!
  },

  /**
   * Delete shop image
   */
  async deleteShopImage(imageId: string): Promise<void> {
    await apiClient.delete(`/shop/shop-images/${imageId}`)
  },

  /**
   * Set gallery image as primary
   */
  async setShopImageAsPrimary(imageId: string): Promise<void> {
    await apiClient.put(`/shop/shop-images/${imageId}/set-primary`)
  },

  /**
   * Update image alt text
   */
  async updateShopImageAltText(imageId: string, altText?: string): Promise<void> {
    await apiClient.put(`/shop/shop-images/${imageId}/alt-text`, { altText })
  },

  // ============================================================================
  // Documents & Certificates
  // ============================================================================

  async getShopDocuments(shopId: string): Promise<ShopDocument[]> {
    return await apiClient.get<ShopDocument[]>(`/shop/shops/${shopId}/documents`)
  },

  async uploadShopDocument(shopId: string, file: File, title: string, isPublic: boolean): Promise<ShopDocument> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', title)
    formData.append('isPublic', String(isPublic))
    return await apiClient.post<ShopDocument>(`/shop/shops/${shopId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  async updateShopDocument(shopId: string, documentId: string, title: string, isPublic: boolean): Promise<ShopDocument> {
    return await apiClient.patch<ShopDocument>(`/shop/shops/${shopId}/documents/${documentId}`, { title, isPublic })
  },

  async deleteShopDocument(shopId: string, documentId: string): Promise<void> {
    await apiClient.delete(`/shop/shops/${shopId}/documents/${documentId}`)
  },

  // ============================================================================
  // Helper Functions
  // ============================================================================

  getStatusLabel(status: ShopStatus): string {
    switch (status) {
      case 'Draft':
        return 'Draft'
      case 'Active':
        return 'Active'
      case 'Suspended':
        return 'Suspended'
      case 'Closed':
        return 'Closed'
      default:
        return 'Unknown'
    }
  },

  getStatusColor(status: ShopStatus): 'secondary' | 'success' | 'warn' | 'danger' {
    switch (status) {
      case 'Draft':
        return 'secondary'
      case 'Active':
        return 'success'
      case 'Suspended':
        return 'warn'
      case 'Closed':
        return 'danger'
      default:
        return 'secondary'
    }
  },

  formatCurrency(amount: number, currency: string = 'GBP'): string {
    return formatMoney(amount, currency)
  }
}
